import { useQuery } from '@tanstack/react-query';
import { useApi } from '../../lib/api';
import { fetchJson } from '../../lib/apiFetchJson';
import { FlowPeopleResponseSchema, type FlowPeopleResponse, type FlowPeopleScope } from '@shared/flow/people';

/**
 * `GET /api/flow/people` — unscoped (the default), every Clay employee with
 * a `salesforce_user` mirror row, backing the person value picker's
 * "Everyone else" A→Z list (Decision 6, U3). Cached indefinitely (project
 * rule: cache-indefinitely + invalidate-on-mutation): this endpoint has no
 * mutation path at all, so there is nothing to ever invalidate.
 *
 * `scope` (Owner/ClayDR filter picker) narrows the directory to exactly the
 * users who can legitimately be assigned to that field (`?scope=`, see
 * `src/shared/flow/people.ts`'s module doc for the business invariant). Each
 * scope — including the unscoped default — gets its own React Query cache
 * entry (`queryKey` varies by scope) so switching a field's scope never
 * serves a stale list captured under a different scope.
 *
 * On failure, callers must degrade to a plain literal text input rather than
 * rendering an empty, dead dropdown — see `filters/PersonValuePicker.tsx`.
 */
export function usePeople(scope?: FlowPeopleScope) {
  const { apiFetch } = useApi();

  return useQuery<FlowPeopleResponse>({
    queryKey: ['flow-people', scope ?? 'all'],
    staleTime: Infinity,
    queryFn: async () => {
      const url = `/api/flow/people${scope ? `?scope=${scope}` : ''}`;
      return fetchJson(apiFetch, FlowPeopleResponseSchema, url);
    },
  });
}
